import {utilsTypeof} from "../shared/utils/utilsTypeof";
import {IBaseColorsConfig} from "../app/styles/themeStyles";
import {initSearchWidget} from "./initSearchWidget";
import {useProductSearchStore} from "../features/productSearch/model/store";
import {useShoppingCartStore} from "../features/shoppingCart/model/store";

/**
 * Класс InitialISearchPanelModule отвечает за конфигурацию и инициализацию виджета поиска.
 *
 * Применяется как API-интерфейс для внешнего хоста: позволяет пошагово настроить поведение и внешний вид
 * виджета до его рендера. После вызова `.render()` — приложение монтируется в определённые DOM-узлы.
 *
 * Используется в сценариях, где скрипт подключается извне и должен быть настроен через JS-интерфейс.
 */

export enum ContainerIdEnum {
    ifurnId = "_isp-root-ifurn-page",
    modalId = "_isp-root-modal",
    pageId = "_isp-root-page",
}

export class InitialISearchPanelModule {
    // Флаг для защиты от повторной инициализации
    private _isInitialized = false;

    // Массив ключей фильтров, которые нужно исключить из UI
    private _excludeFilterFiled: string[] = []

    setExcludeFilterFiled(excludeFilterFiled: string[]) {
        if (utilsTypeof.isArray(excludeFilterFiled)) {
            this._excludeFilterFiled = excludeFilterFiled;
        }
    }

    /**
     * Устанавливает исключения фильтров, которые не должны отображаться в UI.
     * @param excludeFilterFiled - массив ключей фильтров
     */

        // Дополнительные параметры фильтра для API-запросов (например, фильтрация по фирмам  и т.д.)
    private _filterParams: { [key: string]: string[] } = {}

    /**
     * Устанавливает дополнительные параметры фильтра, используемые при запросах.
     * @param filterParams - объект с параметрами фильтра
     */
    setFilterParams(filterParams: { [key: string]: string[] }) {

        if (utilsTypeof.isObject(filterParams)) {
            this._filterParams = filterParams;
        }
    }

    // Канал API (например, 'dev_ifp_en') — используется для смены языка в запросах
    private _apiChannel: string | null = null;

    /**
     * Устанавливает API-канал (например, язык или сегмент).
     * @param channel - строка канала (например 'dev_ifp_en')
     */
    setApiChannel(channel: string) {
        if (utilsTypeof.isValidString(channel)) {
            this._apiChannel = channel;
        }
    }

    // Конфигурация темы (цвета, фон, и т.п.) — используется в ThemeProvider
    private _themeBaseConfig: IBaseColorsConfig = {};

    /**
     * Устанавливает конфигурацию темы (стили цветов и фона).
     * @param themeBaseConfig - объект конфигурации темы
     */
    setThemeBaseConfig(themeBaseConfig: IBaseColorsConfig) {
        if (utilsTypeof.isObject(themeBaseConfig)) {
            this._themeBaseConfig = themeBaseConfig;
        }
    }

    /**
     * Метод, который проверяет на наличение допустимого индентификатора.
     * Может быть вызван только один раз — дальнейшие вызовы будут проигнорированы.
     */
    private checkAllowableContainer(containerId: string): boolean {
        return !!Object.values(ContainerIdEnum).find(id => {
            return containerId === id;
        })
    }

    /**
     * Метод, запускающий отрисовку виджета.
     * Может быть вызван только один раз — дальнейшие вызовы будут проигнорированы.
     */
    render(containerId: string) {
        const isAllowableContainer = this.checkAllowableContainer(containerId);
        if (!isAllowableContainer) {
            console.info(`This id=${containerId} not found`);
            return;
        }
        if (this._isInitialized) {
            console.info(`This widget is already initialized`);
            return
        }
        initSearchWidget({
            themeBaseConfig: this._themeBaseConfig,
            excludeFilterFiled: this._excludeFilterFiled,
            filterParams: this._filterParams,
            apiChannel: this._apiChannel,
            containerId: containerId

        });
        this._isInitialized = true;
    }

    /**
     * Метод, который уничтожает виджет и сбасывает состояние
     * Может быть вызван только после инициализации.
     */

    destroy(containerId: string) {
        const isAllowableContainer = this.checkAllowableContainer(containerId);
        if (!isAllowableContainer) {
            console.info(`This id=${containerId} not found`);
            return;
        }
        if (!this._isInitialized) {
            console.info(`This module is not initialized`);
            return
        }
        const rootElement = document.getElementById(containerId);
        // Удаление DOM-узла
        if (rootElement) {
            rootElement.innerHTML = '';
            this._isInitialized = false;
        }

        useProductSearchStore?.getState?.()?.resetSearch?.();
        useShoppingCartStore?.getState?.()?.resetCart?.();
    }
}